import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	Signal,
	viewChild,
	WritableSignal,
	inject,
} from '@angular/core'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { debounceTime, Subject, takeUntil, timer } from 'rxjs'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import { ThemeEnum } from '@modules/theme-module/enums/theme.enum'
import { LanguageService } from '@modules/lang-module/services/language.service'
import { ThemeService } from '@modules/theme-module/services/theme.service'
import { NgTemplateOutlet } from '@angular/common'
import { TuiSkeleton } from '@taiga-ui/kit'
import { MsInSecond } from '@shared/constants/times/ms-in-second'

@UseLocales({ ru, en })
@Component({
	selector: 'app-info-stocks-statistics',
	templateUrl: './info-stocks-statistics.component.html',
	styleUrl: './info-stocks-statistics.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NgTemplateOutlet, TuiSkeleton],
})
export class InfoStocksStatisticsComponent implements OnInit, OnChanges, OnDestroy {
	protected readonly languageEnum: typeof LanguageEnum = LanguageEnum
	protected readonly themeEnum: typeof ThemeEnum = ThemeEnum
	protected readonly msInSecond: typeof MsInSecond = MsInSecond

	public liveTicker: InputSignal<LiveTickerInterface | null> = input<LiveTickerInterface | null>(null)

	private readonly _languageService: LanguageService = inject<LanguageService>(LanguageService)
	private readonly _themeService: ThemeService = inject<ThemeService>(ThemeService)

	private _fundamentalDataEl: Signal<ElementRef<HTMLDivElement> | undefined> =
		viewChild<ElementRef<HTMLDivElement>>('fundamentalData')

	private destroy$: Subject<void> = new Subject<void>()
	private _needLoadFundamentalData$: Subject<void> = new Subject<void>()

	protected lang: WritableSignal<LanguageEnum> = signal<LanguageEnum>(this.languageEnum.ENGLISH)
	protected theme: WritableSignal<ThemeEnum> = signal<ThemeEnum>(this.themeEnum.LIGHT)

	protected isLoadingFundamentalData: WritableSignal<boolean> = signal<boolean>(false)
	private _dropFundamentalDataEl(): void {
		const fundamentalDataEl: ElementRef<HTMLDivElement> | undefined = this._fundamentalDataEl()
		if (!fundamentalDataEl) return
		fundamentalDataEl.nativeElement.innerHTML = ''
	}

	private _listenLanguage(): void {
		this._languageService.language$.pipe(takeUntil(this.destroy$)).subscribe((response: LanguageEnum): void => {
			this.lang.set(response)
			this._needLoadFundamentalData$.next()
		})
	}

	private _listenTheme(): void {
		this._themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe((response: ThemeEnum): void => {
			this.theme.set(response)
			this._needLoadFundamentalData$.next()
		})
	}

	private listenLoadFundamentalData(): void {
		this._needLoadFundamentalData$.pipe(takeUntil(this.destroy$), debounceTime(0)).subscribe((): void => {
			this._loadFundamentalData()
		})
	}

	private _loadFundamentalData(): void {
		if (!this._fundamentalDataEl()) return
		this._dropFundamentalDataEl()
		const symbol: string | undefined = this.liveTicker()?.ticker
		if (!symbol) return

		this.isLoadingFundamentalData.set(true)

		timer(this.msInSecond)
			.pipe(takeUntil(this.destroy$))
			.subscribe((): void => this.isLoadingFundamentalData.set(false))

		const scriptEl: HTMLScriptElement = document.createElement('script')
		scriptEl.src = 'https://s3.tradingview.com/external-embedding/embed-widget-financials.js'
		scriptEl.async = true
		scriptEl.type = 'text/javascript'
		scriptEl.innerHTML = JSON.stringify({
			symbol,
			displayMode: 'regular',
			isTransparent: true,
			width: '100%',
			height: 2000,
			colorTheme: this.theme(),
			locale: this.lang(),
		})
		this._fundamentalDataEl()?.nativeElement.appendChild(scriptEl)
	}

	ngOnInit(): void {
		this.listenLoadFundamentalData()
		this._listenLanguage()
		this._listenTheme()
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes['liveTicker']) return

		const currentTicker: string | undefined = changes['liveTicker'].currentValue?.ticker
		const previousTicker: string | undefined = changes['liveTicker'].previousValue?.ticker

		if (currentTicker !== previousTicker) {
			this._needLoadFundamentalData$.next()
		}
	}

	ngOnDestroy(): void {
		this._dropFundamentalDataEl()
		this.destroy$.next()
		this.destroy$.complete()
	}
}
